import React, { useState, useRef, useEffect } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import Logo from './Logo';
import NotificationCenter from './NotificationCenter';
import MessagesTab from './MessagesTab';
import { useMessages } from '@/contexts/MessagesContext';
import LanguageSwitcher from './LanguageSwitcher';
import { api } from '@/lib/api';

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const isLoggedIn = user && typeof user === 'object';
  const isAdmin = isLoggedIn && user.role === 'admin';
  const isDonateur = isLoggedIn && user.role === 'donateur';
  // Kiosk-systeemaccount (role=="kiosk", zie models.py::UserRole) mag alles
  // zien maar geen aanvragen/aanbiedingen/zoekertjes — !isKiosk hier volstaat
  // om al die nav-links (Nieuwe aanbieding, Mijn aanbiedingen, Nieuw
  // zoekertje, Mijn zoekertjes) automatisch mee te verbergen, zelfde patroon
  // als de bestaande !isDonateur-uitsluiting.
  const isKiosk = isLoggedIn && user.role === 'kiosk';
  const isValidated = isLoggedIn && user.status === 'validated' && !isDonateur && !isKiosk;
  const canCreateListings = isValidated || isDonateur;
  const displayName = isLoggedIn ? (isDonateur ? user.username : user.firstName) : '';
  const showAanbiedingen = canCreateListings || isValidated;

  const navLink =
    'text-sm tracking-wide text-foreground/80 hover:text-foreground transition-colors industrial-link';
  const activeLink = 'text-foreground';

  const [mobileOpen, setMobileOpen] = useState(false);
  const [aanbiedingenOpen, setAanbiedingenOpen] = useState(false);
  const [mobileAanbiedingenOpen, setMobileAanbiedingenOpen] = useState(false);
  const [overOnsOpen, setOverOnsOpen] = useState(false);
  const [mobileOverOnsOpen, setMobileOverOnsOpen] = useState(false);
  const [nieuwsOpen, setNieuwsOpen] = useState(false);
  const [mobileNieuwsOpen, setMobileNieuwsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  // Gedeeld met MessagesTab.jsx (desktop-icoon) via MessagesContext (fase 9)
  // — geen eigen polling meer hier, en `refresh()` in GesprekDetail.jsx zorgt
  // dat deze meteen bijwerkt zodra een gesprek volledig gelezen is, i.p.v.
  // te wachten op de volgende 60 sec-poll.
  const { hasUnread: messagesUnread } = useMessages();

  useEffect(() => {
    if (!isLoggedIn) {
      setUnreadCount(0);
      return;
    }
    api.get('/notifications/mine')
      .then(({ data }) => {
        setUnreadCount(data.filter((n) => !n.read).length);
      })
      .catch(() => {});
  }, [isLoggedIn, location.pathname]);

  const mobileMenuRef = useRef(null);
  const hamburgerRef = useRef(null);
  const aanbiedingenRef = useRef(null);
  const overOnsRef = useRef(null);
  const nieuwsRef = useRef(null);

  // Sluit mobiel menu bij klik buiten
  useEffect(() => {
    if (!mobileOpen) return;
    const onMouseDown = (e) => {
      if (
        mobileMenuRef.current && !mobileMenuRef.current.contains(e.target) &&
        hamburgerRef.current && !hamburgerRef.current.contains(e.target)
      ) {
        setMobileOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [mobileOpen]);

  // Sluit desktop dropdown bij klik buiten
  useEffect(() => {
    if (!aanbiedingenOpen) return;
    const onMouseDown = (e) => {
      if (aanbiedingenRef.current && !aanbiedingenRef.current.contains(e.target)) {
        setAanbiedingenOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [aanbiedingenOpen]);

  // Sluit over ons dropdown bij klik buiten
  useEffect(() => {
    if (!nieuwsOpen) return;
    const handler = (e) => {
      if (nieuwsRef.current && !nieuwsRef.current.contains(e.target)) setNieuwsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [nieuwsOpen]);

  useEffect(() => {
    if (!overOnsOpen) return;
    const onMouseDown = (e) => {
      if (overOnsRef.current && !overOnsRef.current.contains(e.target)) {
        setOverOnsOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [overOnsOpen]);

  // Sluit alles bij navigatie
  useEffect(() => {
    setMobileOpen(false);
    setAanbiedingenOpen(false);
    setMobileAanbiedingenOpen(false);
    setOverOnsOpen(false);
    setMobileOverOnsOpen(false);
  }, [location.pathname]);

  const mobileItemClass =
    'block px-5 py-3 text-sm tracking-wide text-foreground/80 hover:text-foreground hover:bg-[#ADEBB3] transition-colors border-b border-border last:border-b-0';
  const mobileSubItemClass =
    'block px-8 py-2.5 text-sm text-foreground/70 hover:text-foreground hover:bg-[#ADEBB3] transition-colors border-b border-border';

  const handleMobileLogout = async () => {
    setMobileOpen(false);
    await logout();
    navigate('/');
  };

  return (
    <header className="sticky top-0 z-40 bg-background/95 backdrop-blur-sm border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 flex items-center justify-between h-16">
        <Link to="/" data-testid="header-home-link" className="flex items-center">
          <Logo size="md" />
        </Link>

        {/* DESKTOP NAV */}
        <nav className="hidden md:flex items-center gap-8">
          <NavLink
            to="/catalogus"
            data-testid="nav-catalogus"
            className={({ isActive }) => `${navLink} ${isActive ? activeLink : ''}`}
          >
            {t('nav.catalogus')}
          </NavLink>

          <div className="relative" ref={nieuwsRef}>
            <button
              onClick={() => setNieuwsOpen((v) => !v)}
              className={`${navLink} flex items-center gap-1`}
              data-testid="nav-nieuws-dropdown"
            >
              {t('nav.news')}
              <ChevronDown
                size={14}
                className={`transition-transform duration-200 ${nieuwsOpen ? 'rotate-180' : ''}`}
              />
            </button>
            {nieuwsOpen && (
              <div className="absolute left-0 top-full mt-2 w-44 bg-background border border-border shadow-lg z-50">
                <NavLink
                  to="/nieuws"
                  data-testid="nav-nieuws"
                  className={({ isActive }) =>
                    `block px-4 py-3 text-sm text-foreground/80 hover:text-foreground hover:bg-[#ADEBB3] transition-colors border-b border-border ${isActive ? 'text-foreground font-medium' : ''}`
                  }
                >
                  {t('nav.news')}
                </NavLink>
                <NavLink
                  to="/inspiratie"
                  data-testid="nav-inspiratie"
                  className={({ isActive }) =>
                    `block px-4 py-3 text-sm text-foreground/80 hover:text-foreground hover:bg-[#ADEBB3] transition-colors ${isActive ? 'text-foreground font-medium' : ''}`
                  }
                >
                  {t('nav.inspiratie')}
                </NavLink>
              </div>
            )}
          </div>

          {showAanbiedingen && (
            <div className="relative" ref={aanbiedingenRef}>
              <button
                onClick={() => setAanbiedingenOpen((v) => !v)}
                className={`${navLink} flex items-center gap-1`}
                data-testid="nav-aanbiedingen-dropdown"
              >
                {t('nav.listings')}
                <ChevronDown
                  size={14}
                  className={`transition-transform duration-200 ${aanbiedingenOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {aanbiedingenOpen && (
                <div className="absolute left-0 top-full mt-2 w-52 bg-background border border-border shadow-lg z-50">
                  {canCreateListings && (
                    <NavLink
                      to="/aanbieding/nieuw"
                      data-testid="nav-new-listing"
                      className={({ isActive }) =>
                        `block px-4 py-3 text-sm font-semibold text-foreground hover:bg-[#ADEBB3] transition-colors border-b border-border ${isActive ? 'bg-[#ADEBB3]' : ''}`
                      }
                    >
                      → {t('nav.new_listing')}
                    </NavLink>
                  )}
                  {canCreateListings && (
                    <NavLink
                      to="/mijn-aanbiedingen"
                      data-testid="nav-mijn-aanbiedingen"
                      className={({ isActive }) =>
                        `block px-4 py-3 text-sm text-foreground/80 hover:text-foreground hover:bg-[#ADEBB3] transition-colors border-b border-border ${isActive ? 'text-foreground font-medium' : ''}`
                      }
                    >
                      {t('nav.my_listings')}
                    </NavLink>
                  )}
                  {isValidated && (
                    <NavLink
                      to="/aanvragen"
                      data-testid="nav-aanvragen"
                      className={({ isActive }) =>
                        `block px-4 py-3 text-sm text-foreground/80 hover:text-foreground hover:bg-[#ADEBB3] transition-colors border-b border-border ${isActive ? 'text-foreground font-medium' : ''}`
                      }
                    >
                      {t('nav.my_applications')}
                    </NavLink>
                  )}
                  {isValidated && (
                    <NavLink
                      to="/zoekertje/nieuw"
                      data-testid="nav-new-search-request"
                      className={({ isActive }) =>
                        `block px-4 py-3 text-sm font-semibold text-foreground hover:bg-[#ADEBB3] transition-colors border-b border-border ${isActive ? 'bg-[#ADEBB3]' : ''}`
                      }
                    >
                      → {t('nav.new_search_request')}
                    </NavLink>
                  )}
                  {isValidated && (
                    <NavLink
                      to="/mijn-zoekertjes"
                      data-testid="nav-mijn-zoekertjes"
                      className={({ isActive }) =>
                        `block px-4 py-3 text-sm text-foreground/80 hover:text-foreground hover:bg-[#ADEBB3] transition-colors ${isActive ? 'text-foreground font-medium' : ''}`
                      }
                    >
                      {t('nav.my_search_requests')}
                    </NavLink>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="relative" ref={overOnsRef}>
            <button
              onClick={() => setOverOnsOpen((v) => !v)}
              className={`${navLink} flex items-center gap-1`}
              data-testid="nav-over-ons-dropdown"
            >
              {t('nav.about')}
              <ChevronDown
                size={14}
                className={`transition-transform duration-200 ${overOnsOpen ? 'rotate-180' : ''}`}
              />
            </button>
            {overOnsOpen && (
              <div className="absolute left-0 top-full mt-2 w-44 bg-background border border-border shadow-lg z-50">
                <NavLink
                  to="/over-ons"
                  data-testid="nav-over-ons"
                  className={({ isActive }) =>
                    `block px-4 py-3 text-sm text-foreground/80 hover:text-foreground hover:bg-[#ADEBB3] transition-colors border-b border-border ${isActive ? 'text-foreground font-medium' : ''}`
                  }
                >
                  {t('nav.about')}
                </NavLink>
                <NavLink
                  to="/partners"
                  data-testid="nav-partners"
                  className={({ isActive }) =>
                    `block px-4 py-3 text-sm text-foreground/80 hover:text-foreground hover:bg-[#ADEBB3] transition-colors border-b border-border ${isActive ? 'text-foreground font-medium' : ''}`
                  }
                >
                  {t('nav.partners')}
                </NavLink>
                <NavLink
                  to="/contact"
                  data-testid="nav-contact"
                  className={({ isActive }) =>
                    `block px-4 py-3 text-sm text-foreground/80 hover:text-foreground hover:bg-[#ADEBB3] transition-colors ${isActive ? 'text-foreground font-medium' : ''}`
                  }
                >
                  {t('nav.contact')}
                </NavLink>
              </div>
            )}
          </div>

          {isAdmin && (
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              className={({ isActive }) => `${navLink} ${isActive ? activeLink : ''}`}
            >
              {t('nav.admin')}
            </NavLink>
          )}
        </nav>

        {/* DESKTOP RIGHT GROUP */}
        <div className="hidden md:flex items-center gap-3">
          <LanguageSwitcher className="mr-1" />
          {!isLoggedIn && (
            <>
              <Link to="/login" data-testid="header-login-link" className="text-sm text-foreground/80 hover:text-foreground transition">
                {t('nav.login')}
              </Link>
              <Link to="/donateur/registreer" data-testid="header-donateur-btn" className="text-sm text-foreground/80 hover:text-foreground transition">
                {t('nav.donate_material')}
              </Link>
              <Link to="/registreer" data-testid="header-register-link" className="btn-primary !py-2 !px-4 text-xs">
                {t('nav.join_member')}
              </Link>
            </>
          )}
          {isLoggedIn && (
            <>
              <MessagesTab />
              <NotificationCenter />
              <Link to="/profiel" data-testid="header-profile-link" className="text-sm text-foreground/80 hover:text-foreground transition hidden sm:inline">
                {displayName}
              </Link>
              <button
                onClick={async () => { await logout(); navigate('/'); }}
                data-testid="header-logout-btn"
                className="text-sm text-foreground/70 hover:text-foreground transition"
              >
                {t('nav.logout')}
              </button>
            </>
          )}
        </div>

        {/* MOBILE HAMBURGER */}
        <div className="md:hidden relative flex items-center gap-2">
          {isLoggedIn && messagesUnread && !mobileOpen && (
            // Fase 9: enkel een stip, geen cijfer (zelfde als MessagesTab.jsx).
            <Link
              to="/berichten"
              className="relative p-1"
              data-testid="mobile-messages-badge"
            >
              <span className="block w-2.5 h-2.5 bg-red-600 rounded-full" />
            </Link>
          )}
          {isLoggedIn && unreadCount > 0 && !mobileOpen && (
            <Link
              to="/notificaties"
              className="relative p-1"
              data-testid="mobile-notif-badge"
            >
              <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-medium">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            </Link>
          )}
          <button
            ref={hamburgerRef}
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? 'Menu sluiten' : 'Menu openen'}
            aria-expanded={mobileOpen}
            data-testid="hamburger-button"
            className="p-2 -mr-2 text-foreground hover:bg-muted transition-colors"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>

          {mobileOpen && (
            <div
              ref={mobileMenuRef}
              data-testid="mobile-menu-dropdown"
              className="absolute right-0 top-full mt-2 w-64 bg-background border border-border shadow-lg animate-fade-in"
            >
              {isLoggedIn && (
                <div className="px-5 py-3 border-b border-border bg-muted/40">
                  <p className="text-xs text-muted-foreground uppercase tracking-widest">{t('nav.logged_in_as')}</p>
                  <p className="text-sm font-medium mt-0.5 truncate">
                    {isDonateur ? user.username : `${user.firstName || ''} ${user.lastName || ''}`.trim()}
                  </p>
                </div>
              )}

              <NavLink
                to="/catalogus"
                data-testid="mobile-nav-catalogus"
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) => `${mobileItemClass} ${isActive ? 'text-foreground bg-muted/50 font-medium' : ''}`}
              >
                {t('nav.catalogus')}
              </NavLink>

              <button
                onClick={() => setMobileNieuwsOpen((v) => !v)}
                className={`${mobileItemClass} w-full text-left flex items-center justify-between`}
                data-testid="mobile-nav-nieuws-dropdown"
              >
                {t('nav.news')}
                <ChevronDown
                  size={14}
                  className={`transition-transform duration-200 ${mobileNieuwsOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {mobileNieuwsOpen && (
                <>
                  <NavLink
                    to="/nieuws"
                    data-testid="mobile-nav-nieuws"
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) => `${mobileSubItemClass} ${isActive ? 'text-foreground font-medium' : ''}`}
                  >
                    → {t('nav.news')}
                  </NavLink>
                  <NavLink
                    to="/inspiratie"
                    data-testid="mobile-nav-inspiratie"
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) => `${mobileSubItemClass} ${isActive ? 'text-foreground font-medium' : ''}`}
                  >
                    → {t('nav.inspiratie')}
                  </NavLink>
                </>
              )}

              {showAanbiedingen && (
                <>
                  <button
                    onClick={() => setMobileAanbiedingenOpen((v) => !v)}
                    className={`${mobileItemClass} w-full text-left flex items-center justify-between`}
                    data-testid="mobile-nav-aanbiedingen"
                  >
                    {t('nav.listings')}
                    <ChevronDown
                      size={14}
                      className={`transition-transform duration-200 ${mobileAanbiedingenOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {mobileAanbiedingenOpen && (
                    <>
                      {canCreateListings && (
                        <NavLink
                          to="/aanbieding/nieuw"
                          data-testid="mobile-nav-new-listing"
                          onClick={() => setMobileOpen(false)}
                          className={({ isActive }) => `${mobileSubItemClass} font-semibold text-foreground ${isActive ? 'bg-[#ADEBB3]' : ''}`}
                        >
                          → {t('nav.new_listing')}
                        </NavLink>
                      )}
                      {canCreateListings && (
                        <NavLink
                          to="/mijn-aanbiedingen"
                          data-testid="mobile-nav-mijn-aanbiedingen"
                          onClick={() => setMobileOpen(false)}
                          className={({ isActive }) => `${mobileSubItemClass} ${isActive ? 'text-foreground font-medium' : ''}`}
                        >
                          → {t('nav.my_listings')}
                        </NavLink>
                      )}
                      {isValidated && (
                        <NavLink
                          to="/aanvragen"
                          data-testid="mobile-nav-aanvragen"
                          onClick={() => setMobileOpen(false)}
                          className={({ isActive }) => `${mobileSubItemClass} ${isActive ? 'text-foreground font-medium' : ''}`}
                        >
                          → {t('nav.my_applications')}
                        </NavLink>
                      )}
                      {isValidated && (
                        <NavLink
                          to="/zoekertje/nieuw"
                          data-testid="mobile-nav-new-search-request"
                          onClick={() => setMobileOpen(false)}
                          className={({ isActive }) => `${mobileSubItemClass} font-semibold text-foreground ${isActive ? 'bg-[#ADEBB3]' : ''}`}
                        >
                          → {t('nav.new_search_request')}
                        </NavLink>
                      )}
                      {isValidated && (
                        <NavLink
                          to="/mijn-zoekertjes"
                          data-testid="mobile-nav-mijn-zoekertjes"
                          onClick={() => setMobileOpen(false)}
                          className={({ isActive }) => `${mobileSubItemClass} ${isActive ? 'text-foreground font-medium' : ''}`}
                        >
                          → {t('nav.my_search_requests')}
                        </NavLink>
                      )}
                    </>
                  )}
                </>
              )}

              <button
                onClick={() => setMobileOverOnsOpen((v) => !v)}
                className={`${mobileItemClass} w-full text-left flex items-center justify-between`}
                data-testid="mobile-nav-over-ons-dropdown"
              >
                {t('nav.about')}
                <ChevronDown
                  size={14}
                  className={`transition-transform duration-200 ${mobileOverOnsOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {mobileOverOnsOpen && (
                <>
                  <NavLink
                    to="/over-ons"
                    data-testid="mobile-nav-over-ons"
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) => `${mobileSubItemClass} ${isActive ? 'text-foreground font-medium' : ''}`}
                  >
                    → {t('nav.about')}
                  </NavLink>
                  <NavLink
                    to="/partners"
                    data-testid="mobile-nav-partners"
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) => `${mobileSubItemClass} ${isActive ? 'text-foreground font-medium' : ''}`}
                  >
                    → {t('nav.partners')}
                  </NavLink>
                  <NavLink
                    to="/contact"
                    data-testid="mobile-nav-contact"
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) => `${mobileSubItemClass} ${isActive ? 'text-foreground font-medium' : ''}`}
                  >
                    → {t('nav.contact')}
                  </NavLink>
                </>
              )}

              {isAdmin && (
                <NavLink
                  to="/admin"
                  data-testid="mobile-nav-admin"
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) => `${mobileItemClass} ${isActive ? 'text-foreground bg-muted/50 font-medium' : ''}`}
                >
                  {t('nav.admin')}
                </NavLink>
              )}

              {isLoggedIn && (
                <NavLink
                  to="/berichten"
                  data-testid="mobile-nav-berichten"
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `${mobileItemClass} flex items-center justify-between ${isActive ? 'text-foreground bg-muted/50 font-medium' : ''}`
                  }
                >
                  <span>{t('nav.messages')}</span>
                  {messagesUnread && <span className="w-2.5 h-2.5 bg-red-600 rounded-full" />}
                </NavLink>
              )}

              {isLoggedIn && (
                <NavLink
                  to="/notificaties"
                  data-testid="mobile-nav-notificaties"
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `${mobileItemClass} flex items-center justify-between ${isActive ? 'text-foreground bg-muted/50 font-medium' : ''}`
                  }
                >
                  <span>{t('nav.notifications')}</span>
                  {unreadCount > 0 && (
                    <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-medium">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </NavLink>
              )}

              {isLoggedIn && (
                <NavLink
                  to="/profiel"
                  data-testid="mobile-nav-profiel"
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) => `${mobileItemClass} ${isActive ? 'text-foreground bg-muted/50 font-medium' : ''}`}
                >
                  {t('nav.profile')}
                </NavLink>
              )}

              {!isLoggedIn && (
                <>
                  <Link to="/login" onClick={() => setMobileOpen(false)} data-testid="mobile-header-login-link" className={mobileItemClass}>
                    {t('nav.login')}
                  </Link>
                  <Link to="/donateur/registreer" onClick={() => setMobileOpen(false)} data-testid="mobile-header-donateur-btn" className={mobileItemClass}>
                    {t('nav.give_gift')}
                  </Link>
                  <Link to="/registreer" onClick={() => setMobileOpen(false)} data-testid="mobile-header-register-link" className={`${mobileItemClass} font-medium text-foreground`}>
                    {t('nav.join_member')} →
                  </Link>
                </>
              )}

              {isLoggedIn && (
                <button
                  onClick={handleMobileLogout}
                  data-testid="mobile-header-logout-btn"
                  className={`${mobileItemClass} w-full text-left text-destructive`}
                >
                  {t('nav.logout')}
                </button>
              )}

              <div className="px-5 py-3 border-t border-border flex items-center justify-between">
                <span className="text-xs text-muted-foreground uppercase tracking-widest">{t('common.language')}</span>
                <LanguageSwitcher />
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
